import {
  ScienceOutlined as FlaskOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  Remove as RemoveIcon,
  ReplayOutlined as ReplayOutlinedIcon,
  ScienceOutlined,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material'
import { Button } from '@mui/material'
import { ExperimentDisposition, MicroLearningDetailResponse } from '../../../api/models'
import styles from '../MicroLearningDetail.module.css'
import { MicroLearningActionModal } from './MicroLearningActionModal'

type DispositionByCode = Record<string, ExperimentDisposition | undefined>

type ExperimentDispositionModalProps = {
  open: boolean
  data: MicroLearningDetailResponse | null
  dispositions: DispositionByCode
  isSaving: boolean
  onSetDisposition: (code: string, disposition: ExperimentDisposition) => void
  onSetAllDisposition: (disposition: ExperimentDisposition) => void
  onClose: () => void
  onSave: () => void
}

export default function ExperimentDispositionModal({
  open,
  data,
  dispositions,
  isSaving,
  onSetDisposition,
  onSetAllDisposition,
  onClose,
  onSave,
}: ExperimentDispositionModalProps) {
  const experiments = data?.experiments ?? []
  const selectedCount = experiments.filter(experiment => dispositions[experiment.code]).length
  const allSelected = experiments.length > 0 && selectedCount === experiments.length
  const reclusterCount = experiments.filter(experiment => dispositions[experiment.code] === 'recluster').length
  const excludeCount = experiments.filter(experiment => dispositions[experiment.code] === 'exclude').length

  return (
    <MicroLearningActionModal
      open={open}
      onClose={onClose}
      onConfirm={onSave}
      confirmText='Save'
      confirmDisabled={!allSelected || isSaving}
      theme='danger'
      headerIcon={<ScienceOutlined style={{ color: 'rgba(244, 0, 9, 1)', fontSize: 20 }} />}
      title='Experiment Disposition'
      subtitle={`To dismiss ${data?.ml_id || 'this micro-learning'}, decide what happens to its experiments.`}
      mlId={data?.ml_id || '—'}
      mlTitle={data?.generation?.title || data?.title || 'Micro-Learning'}
      score={data?.score}
      warningBanner={
        <div className={styles.dismissWarningBanner}>
          <WarningAmberIcon fontSize='small' className={styles.warningBannerIcon} />
          <span>
            To finish dismissing <strong>{data?.ml_id || 'this micro-learning'}</strong>, you need to manage its active
            experiments.
          </span>
        </div>
      }
      showMlCard={false}>
      <p className={styles.dispositionInstruction}>
        For each experiment, click <ReplayOutlinedIcon fontSize='inherit' className={styles.dispositionInstructionIconRecluster} /> to
        return it to the clustering pipeline or <RemoveIcon fontSize='inherit' className={styles.dispositionInstructionIconExclude} /> to
        permanently exclude it. You can click &quot;All&quot; above to apply to all at once.
      </p>

      <div className={styles.dispositionHeaderRow}>
        <span className={styles.dispositionSectionTitle}>Experiments</span>
        <div className={styles.dispositionBulkActions}>
          <button type='button' className={styles.allActionButton} onClick={() => onSetAllDisposition('recluster')}>
            All
          </button>
          <button type='button' className={styles.allActionButton} onClick={() => onSetAllDisposition('exclude')}>
            All
          </button>
        </div>
      </div>

      <div className={styles.dispositionList}>
        {experiments.map(experiment => {
          const selectedDisposition = dispositions[experiment.code]
          const rowClassName =
            selectedDisposition === 'exclude'
              ? styles.dispositionRowExclude
              : selectedDisposition === 'recluster'
                ? styles.dispositionRowRecluster
                : ''
          return (
            <div key={experiment.code} className={`${styles.dispositionRow} ${rowClassName}`}>
              <div className={styles.dispositionExperimentCode}>
                <FlaskOutlinedIcon fontSize='small' />
                {experiment.code}
                {selectedDisposition && (
                  <span
                    className={
                      selectedDisposition === 'exclude'
                        ? styles.dispositionBadgeExclude
                        : styles.dispositionBadgeRecluster
                    }>
                    {selectedDisposition === 'exclude' ? 'Exclude' : 'Re-cluster'}
                  </span>
                )}
              </div>     
              <div className={styles.dispositionRowActions}>
                <Button
                  size='small'
                  className={
                    selectedDisposition === 'recluster'
                      ? styles.dispositionButtonReclusterActive
                      : styles.dispositionButtonRecluster
                  }
                  onClick={() => onSetDisposition(experiment.code, 'recluster')}>
                  <ReplayOutlinedIcon fontSize='small' />
                </Button>
                <Button
                  size='small'
                  className={
                    selectedDisposition === 'exclude'
                      ? styles.dispositionButtonExcludeActive
                      : styles.dispositionButtonExclude
                  }
                  onClick={() => onSetDisposition(experiment.code, 'exclude')}>
                  <RemoveIcon fontSize='small' />
                </Button>
              </div>
            </div>
          )
        })}
      </div>

      <div className={styles.dispositionSummary}>
        {reclusterCount > 0 && (
          <span className={styles.dispositionSummaryRecluster}>
            <ReplayOutlinedIcon fontSize='small' /> {reclusterCount} re-clustering
          </span>
        )}
        {excludeCount > 0 && (
          <span className={styles.dispositionSummaryExclude}>
            <RemoveIcon fontSize='small' /> {excludeCount} excluding
          </span>
        )}
      </div>

      <div className={styles.dispositionInfoCallout}>
        <InfoOutlinedIcon fontSize='small' />
        Dismissing the Micro-Learning but keeping all experiments for re-clustering may result in a very similar
        Micro-Learning being created with the same or similar group.
      </div>
    </MicroLearningActionModal>
  )
}
